import React, { useEffect, useState, useRef } from "react";
import {
  Container, Typography, Box, Paper, Slider, Chip,
  FormControlLabel, Switch, Button, useMediaQuery,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AuthHeader from "../components/AuthHeader";

// 中2(J2)「パッセージ」専用ページ。
// 重要例文ビューアの見え方を踏襲しつつ、パッセージ向けに調整:
//  - 英語を上・日本語を下に表示（重要例文とは上下逆）
//  - 表示切替は「日本語」の表示/非表示（重要例文は英語）
//  - 表示元セレクタ・ネイティブ音声は無し
//  - 週選択 / 再生速度 / 連続再生 / ループ再生 は維持
function PassageViewerJ2Page({ API_BASE, AUDIO_BASE }) {
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const weekNumParam = searchParams.get("week_num");
  const hasWeekFilter = Boolean(weekNumParam);

  const [sentences, setSentences] = useState([]);
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(weekNumParam || "");
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [loopAll, setLoopAll] = useState(false);
  const [autoNext, setAutoNext] = useState(false);
  const [showJapanese, setShowJapanese] = useState(true);
  const [individualJapaneseShow, setIndividualJapaneseShow] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [playingKey, setPlayingKey] = useState(null);
  const audioRefs = useRef({});
  const sentenceRefs = useRef({});
  const isPlayingRef = useRef(false);
  const [autoNextTimer, setAutoNextTimer] = useState(null);

  // ログイン状態チェック
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/passage_viewer_j2' }, replace: true });
    }
  }, [isAuthenticated, navigate]);

  // 週リスト取得
  useEffect(() => {
    if (hasWeekFilter) {
      setSelectedWeek(weekNumParam);
    } else {
      fetch(`${API_BASE}/j2/passage_weeks`)
        .then((res) => res.json())
        .then((data) => {
          const weeks = Array.isArray(data) ? data.map((w) => w.week_num) : [];
          setWeekOptions(weeks);
          if (weeks.length > 0) {
            setSelectedWeek(weeks[0]);
          }
        })
        .catch((err) => console.error("week fetch error:", err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // パッセージ取得
  useEffect(() => {
    if (selectedWeek) {
      fetch(`${API_BASE}/j2/passages_by_week/${selectedWeek}`)
        .then((res) => res.json())
        .then((data) => {
          const sentenceData = Array.isArray(data) ? data : [];
          setSentences(sentenceData);
          setIndividualJapaneseShow(new Array(sentenceData.length).fill(true));
        })
        .catch((err) => console.error("API fetch error:", err));
    }
  }, [API_BASE, selectedWeek]);

  useEffect(() => {
    Object.values(audioRefs.current).forEach((audio) => {
      if (audio) audio.playbackRate = playbackRate;
    });
  }, [playbackRate, sentences]);

  useEffect(() => {
    if (currentIndex !== null && sentenceRefs.current[currentIndex]) {
      sentenceRefs.current[currentIndex].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentIndex]);

  const handleGlobalJapaneseToggle = (checked) => {
    setShowJapanese(checked);
    setIndividualJapaneseShow(new Array(sentences.length).fill(checked));
  };

  const handleIndividualJapaneseToggle = (index, checked) => {
    const newIndividualShow = [...individualJapaneseShow];
    newIndividualShow[index] = checked;
    setIndividualJapaneseShow(newIndividualShow);

    const allTrue = newIndividualShow.every((show) => show);
    const allFalse = newIndividualShow.every((show) => !show);
    if (allTrue) {
      setShowJapanese(true);
    } else if (allFalse) {
      setShowJapanese(false);
    }
  };

  const stopAllAudio = () => {
    Object.values(audioRefs.current).forEach((audio) => {
      if (audio && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    if (autoNextTimer) {
      clearTimeout(autoNextTimer);
      setAutoNextTimer(null);
    }
    isPlayingRef.current = false;
    setCurrentIndex(null);
    setPlayingKey(null);
  };

  const handlePlay = (index, audioIndex = 0) => {
    stopAllAudio();

    const key = `${index}_${audioIndex}`;
    const audio = audioRefs.current[key];
    const currentSentence = sentences[index];

    if (audio && currentSentence) {
      audio.play();
      isPlayingRef.current = true;
      setCurrentIndex(index);
      setPlayingKey(key);
    }
  };

  const handleEnded = () => {
    if (!isPlayingRef.current || !playingKey) return;

    const [indexStr, audioIndexStr] = playingKey.split("_");
    const index = parseInt(indexStr);
    const audioIndex = parseInt(audioIndexStr);
    const currentItem = sentences[index];
    const nextAudioIndex = audioIndex + 1;

    if (Array.isArray(currentItem.audio_files) && nextAudioIndex < currentItem.audio_files.length) {
      if (autoNext) {
        const timer = setTimeout(() => handlePlay(index, nextAudioIndex), 1000);
        setAutoNextTimer(timer);
      } else {
        isPlayingRef.current = false;
        setCurrentIndex(null);
        setPlayingKey(null);
      }
    } else {
      const nextIndex = index + 1;
      if (autoNext && nextIndex < sentences.length) {
        const timer = setTimeout(() => handlePlay(nextIndex, 0), 1000);
        setAutoNextTimer(timer);
      } else if (loopAll) {
        const timer = setTimeout(() => handlePlay(index, 0), 1000);
        setAutoNextTimer(timer);
      } else {
        isPlayingRef.current = false;
        setCurrentIndex(null);
        setPlayingKey(null);
      }
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <AuthHeader />
      <Container maxWidth="md" sx={{ paddingBottom: 24 }}>
        <Typography variant="h5" sx={{ mt: 2, mb: 2 }}>
          中2 パッセージ
        </Typography>

        {!hasWeekFilter && (
          <Box sx={{ marginBottom: 4 }}>
            <FormControl fullWidth>
              <InputLabel id="week-label">週番号を選択</InputLabel>
              <Select
                labelId="week-label"
                value={selectedWeek}
                label="週番号を選択"
                onChange={(e) => setSelectedWeek(e.target.value)}
              >
                {weekOptions.map((week, i) => (
                  <MenuItem key={i} value={week}>
                    {week}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Box sx={{ marginBottom: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showJapanese}
                onChange={(e) => handleGlobalJapaneseToggle(e.target.checked)}
              />
            }
            label="全て日本語表示"
          />
        </Box>

        <Box display="flex" flexDirection="column" gap={3}>
          {Array.isArray(sentences) &&
            sentences.map((item, index) => (
              <Paper
                key={item.id || index}
                ref={(el) => (sentenceRefs.current[index] = el)}
                sx={{
                  padding: 2,
                  backgroundColor: currentIndex === index ? "#e3f2fd" : "inherit",
                }}
              >
                <Chip label={item.sentence_index} color="primary" size="small" sx={{ mb: 1 }} />

                <Typography variant="h6" gutterBottom sx={{ lineHeight: 1.5 }}>
                  {item.sentence}
                </Typography>

                <Box sx={{ marginBottom: 2 }}>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    gutterBottom
                    sx={{
                      visibility: individualJapaneseShow[index] ? 'visible' : 'hidden',
                      minHeight: '1.5rem',
                      lineHeight: 1.5
                    }}
                  >
                    {item.sentence_jp || ' '}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={individualJapaneseShow[index] || false}
                        onChange={(e) => handleIndividualJapaneseToggle(index, e.target.checked)}
                      />
                    }
                    label="日本語"
                    sx={{ marginRight: 2 }}
                  />

                  {Array.isArray(item.audio_files) && item.audio_files.map((file, i) => (
                    <React.Fragment key={`${index}_${i}`}>
                      {playingKey === `${index}_${i}` ? (
                        <Button variant="outlined" color="error" onClick={stopAllAudio} sx={{ mr: 1 }}>
                          停止
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={() => handlePlay(index, i)}
                          sx={{ mr: 1 }}
                        >
                          再生
                        </Button>
                      )}
                      <audio
                        style={{ display: "none" }}
                        ref={(el) => (audioRefs.current[`${index}_${i}`] = el)}
                        onEnded={handleEnded}
                      >
                        <source src={`${AUDIO_BASE}/audio_clipped/${file}`} type="audio/mpeg" />
                      </audio>
                    </React.Fragment>
                  ))}
                </Box>
              </Paper>
            ))}
        </Box>

        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            backgroundColor: "#fff",
            borderTop: "1px solid #ccc",
            padding: 2,
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
            gap: 2,
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <Box minWidth={200}>
            <Typography gutterBottom>再生速度：{playbackRate.toFixed(1)}x</Typography>
            <Slider
              value={playbackRate}
              onChange={(_, val) => setPlaybackRate(val)}
              min={0.5}
              max={1.5}
              step={0.1}
              valueLabelDisplay="auto"
              marks={[{ value: 0.5, label: "0.5x" }, { value: 1.0, label: "1.0x" }, { value: 1.5, label: "1.5x" }]}
            />
          </Box>
          <Box display="flex" flexDirection="row" gap={4}>
            <FormControlLabel
              control={<Switch checked={loopAll} onChange={(e) => setLoopAll(e.target.checked)} />}
              label="ループ再生"
            />
            <FormControlLabel
              control={<Switch checked={autoNext} onChange={(e) => setAutoNext(e.target.checked)} />}
              label="連続再生"
            />
          </Box>
        </Box>
      </Container>
    </>
  );
}

export default PassageViewerJ2Page;
